import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {PREFERENCES_SURVEY_MODAL_TEST_IDS} from "@pg-mono/test-ids/src/rp/common/test_id";
import {Currency} from "@pg-rp/common-types";

import {useGetClientInfoQuery} from "../../../client/api/get_client_info";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {type IClientPayload, useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../../for_you/api/for_you_client";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {OfferType} from "../../../offer/types/OfferType";
import {useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {UserPreferencesFormProvider} from "../../../user/components/preferences/UserPreferencesFormProvider";
import {PreferencesFormWithPriceHeader} from "../../../user/components/preferences_form/PreferencesFormWithPriceHeader";
import {PreferencesFormVariantB} from "../../../user/components/PreferencesFormVariantB";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../../../user/constants/preferences/application_preferences_form";
import {useSurveyDefaultValues} from "../../../user/hooks/use_survey_default_values";
import {hitGtmSurveyBasicSubmit} from "../../../user/tracking/preferences/preferences_hit_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../user/tracking/preferences/preferences_hit_survey_click";
import {hitGtmSurveyClose} from "../../../user/tracking/preferences/preferences_hit_survey_close";
import {hitGtmSurveyView} from "../../../user/tracking/preferences/preferences_hit_survey_view";
import {IPreferencesDefaultValues} from "../../../user/types/IPreferencesDefaultValues";
import {savePreferencesVariantB} from "../../state/registration_flow_variantB_slice";
import {AuthModalVariant} from "../auth_modal/AuthModalHeaderVariants";

interface IProps {
    isOpen: boolean;
    isPropertyApplication: boolean;
    offerId?: number;
    onSuccess?: () => void;
}

export const RegistrationPreferencesModalVariantB = (props: IProps) => {
    const {isOpen, isPropertyApplication, offerId, onSuccess} = props;

    const [postClient, {isSuccess: isPostSuccess, isLoading: isPostLoading}] = useCreateForYouClientMutation();
    const [updateClient, {isSuccess: isUpdateSuccess, isLoading: isUpdateLoading}] = useUpdateForYouClientMutation();

    const {data: userProfile} = useGetUserProfileQuery();
    const {data: clientInfo} = useGetClientInfoQuery();

    const flowState = useAppSelector((state) => state.registrationFlowVariantB);

    const maxBudget = PREFERENCES_FORM_MAX_DEFAULT;
    const currency = Currency.PLN;

    const {defaultValues, displayedPrice, isLoading} = useSurveyDefaultValues({maxBudget});

    const dispatch = useDispatch();

    const getEventCategory = (): string => {
        if (flowState.userSource === UserSource.FAVOURITE_PROPERTY || flowState.userSource === UserSource.FAVOURITE_OFFER) {
            return "favourite";
        }

        if (flowState.socialMediaType === "google") {
            return "google";
        }
        if (flowState.socialMediaType === "facebook") {
            return "facebook";
        }

        return "email";
    };

    const eventCategory = getEventCategory();

    useEffect(() => {
        if (isOpen) {
            hitGtmSurveyView(flowState.gtmSourceSection || eventCategory);
        }
    }, [isOpen, eventCategory]);

    useEffect(() => {
        if (isPostSuccess || isUpdateSuccess) {
            onSuccess?.();
        }
    }, [isPostSuccess, isUpdateSuccess, onSuccess]);

    const onSubmit = async (values: IPreferencesDefaultValues, isExplicitSubmit = false) => {
        if (!userProfile) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return;
        }

        if (isExplicitSubmit) {
            hitGtmSurveyBasicSubmit(values, flowState.gtmSourceSection || eventCategory);
        }

        if (!isExplicitSubmit) {
            hitGtmSurveyClose(flowState.gtmSourceSection || eventCategory);
        }

        const regions = values.regionSelector.map((region) => region.value);

        const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : 1;
        const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : 5;
        const rooms = {lower, upper};

        const price = values.budget && values.budget !== maxBudget ? {lower: 0, upper: values.budget} : null;
        const systemComments = isExplicitSubmit ? "Ankieta świadomie zapisana przez użytkownika" : "Ankieta wysłana poprzez zamknięcie modala";

        const postData: IClientPayload = {
            email: userProfile.email,
            name: userProfile.name,
            phone: userProfile.phone || "",
            marketing_consent: userProfile.email_marketing_consent,
            financing_services: values.creditPlan,
            regions: regions.length ? regions : [],
            rooms: rooms,
            types: [OfferType.FLAT, OfferType.HOUSE],
            price: price || {lower: 0, upper: PREFERENCES_FORM_MAX_DEFAULT},
            area: {lower: 0, upper: 0},
            loan_installment: {lower: 0, upper: 0},
            distance: 0,
            system_comments: systemComments,
            explicit_submit: isExplicitSubmit
        };

        try {
            if (!!clientInfo) {
                await updateClient(postData).unwrap();
            } else {
                // Disable HomeMatch for clients created via this flow
                postData.no_hm_enable = true;

                await postClient(postData).unwrap();
            }

            dispatch(
                savePreferencesVariantB({
                    regions: regions.length ? regions : [],
                    price: !values.budget || values.budget >= maxBudget ? undefined : values.budget,
                    rooms: rooms
                })
            );
        } catch (err) {
            notifyBugsnag(err, "RegistrationPreferencesModal save failed");

            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick(flowState.gtmSourceSection || eventCategory, value, section);
    };

    return (
        <Modal isOpen={isOpen} className={css({sm: {borderRadius: 2}})}>
            <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.WRAPPER}>
                <Modal.Layout.EmptyResponsive closeButtonVariant="none" className={modalStyle}>
                    <div className={contentStyle}>
                        {/* We need to wait until `defaultValues` is built, so that the form gets the correct values */}
                        {isLoading || isPostLoading || isUpdateLoading ? (
                            <div className={loaderWrapperStyle}>
                                <Loader size="lg" />
                            </div>
                        ) : (
                            <UserPreferencesFormProvider defaultValues={defaultValues}>
                                {() => {
                                    return (
                                        <PreferencesFormVariantB
                                            onSubmit={(values, isExplicitSubmit) => onSubmit(values, isExplicitSubmit)}
                                            header={
                                                flowState.titleVariant === AuthModalVariant.CONSTRUCTION_PHOTOS ? null : (
                                                    <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.HEADER_BOX}>
                                                        <PreferencesFormWithPriceHeader
                                                            isPropertyApplication={isPropertyApplication}
                                                            offerId={offerId}
                                                            price={displayedPrice}
                                                        />
                                                    </div>
                                                )
                                            }
                                            topFormContent={
                                                <div className={headerStyle}>
                                                    <div className={headerTextStyle}>
                                                        <Text variant="headline_6" as="p">
                                                            Potwierdź swoje preferencje
                                                        </Text>
                                                        <Text variant="info_txt_1">
                                                            Potwierdź swój budżet lokalizacji i liczbę pokoi, aby otrzymać lepiej spersonalizowane oferty.
                                                        </Text>
                                                    </div>
                                                </div>
                                            }
                                            currency={currency}
                                            maxBudget={maxBudget}
                                            formWrapperClassName={formWrapperClassName}
                                            onFieldInteraction={handleFieldInteraction}
                                            showMarketingConsents
                                            locationFieldTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.LOCATION_FIELD}
                                            budgetInputTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.BUDGET_INPUT}
                                            creditPlanSwitchTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CREDIT_PLAN_SWITCH}
                                            savePreferencesButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.SAVE_PREFERENCES_BUTTON}
                                            closeButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CLOSE_BUTTON}
                                        />
                                    );
                                }}
                            </UserPreferencesFormProvider>
                        )}
                    </div>
                </Modal.Layout.EmptyResponsive>
            </div>
        </Modal>
    );
};

const modalStyle = css({
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    p: 0,

    sm: {
        width: "544px"
    }
});

const contentStyle = css({
    width: "100%",
    p: 0,
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    gap: 4,

    sm: {
        width: "544px",
        p: 3
    }
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "100vh",

    sm: {
        minHeight: "300px"
    }
});

const formWrapperClassName = css({
    p: 2,

    md: {
        p: 0
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {PREFERENCES_SURVEY_MODAL_TEST_IDS} from "@pg-mono/test-ids/src/rp/common/test_id";
import {Currency} from "@pg-rp/common-types";

import {useGetClientInfoQuery} from "../../../client/api/get_client_info";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {type IClientPayload, useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../../for_you/api/for_you_client";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {OfferType} from "../../../offer/types/OfferType";
import {useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {UserPreferencesFormProvider} from "../../../user/components/preferences/UserPreferencesFormProvider";
import {PreferencesFormWithPriceHeader} from "../../../user/components/preferences_form/PreferencesFormWithPriceHeader";
import {PreferencesFormVariantB} from "../../../user/components/PreferencesFormVariantB";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../../../user/constants/preferences/application_preferences_form";
import {useSurveyDefaultValues} from "../../../user/hooks/use_survey_default_values";
import {hitGtmSurveyBasicSubmit} from "../../../user/tracking/preferences/preferences_hit_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../user/tracking/preferences/preferences_hit_survey_click";
import {hitGtmSurveyClose} from "../../../user/tracking/preferences/preferences_hit_survey_close";
import {hitGtmSurveyView} from "../../../user/tracking/preferences/preferences_hit_survey_view";
import {IPreferencesDefaultValues} from "../../../user/types/IPreferencesDefaultValues";
import {savePreferencesVariantB} from "../../state/registration_flow_variantB_slice";
import {AuthModalVariant} from "../auth_modal/AuthModalHeaderVariants";

interface IProps {
    isOpen: boolean;
    isPropertyApplication: boolean;
    offerId?: number;
    onSuccess?: () => void;
}

export const RegistrationPreferencesModalVariantB = (props: IProps) => {
    const {isOpen, isPropertyApplication, offerId, onSuccess} = props;

    const [postClient, {isSuccess: isPostSuccess, isLoading: isPostLoading}] = useCreateForYouClientMutation();
    const [updateClient, {isSuccess: isUpdateSuccess, isLoading: isUpdateLoading}] = useUpdateForYouClientMutation();

    const {data: userProfile} = useGetUserProfileQuery();
    const {data: clientInfo} = useGetClientInfoQuery();

    const flowState = useAppSelector((state) => state.registrationFlowVariantB);

    const maxBudget = PREFERENCES_FORM_MAX_DEFAULT;
    const currency = Currency.PLN;

    const {defaultValues, displayedPrice, isLoading} = useSurveyDefaultValues({maxBudget});

    const dispatch = useDispatch();

    const getEventCategory = (): string => {
        if (flowState.userSource === UserSource.FAVOURITE_PROPERTY || flowState.userSource === UserSource.FAVOURITE_OFFER) {
            return "favourite";
        }

        if (flowState.socialMediaType === "google") {
            return "google";
        }
        if (flowState.socialMediaType === "facebook") {
            return "facebook";
        }

        return "email";
    };

    const eventCategory = getEventCategory();

    useEffect(() => {
        if (isOpen) {
            hitGtmSurveyView(flowState.gtmSourceSection || eventCategory);
        }
    }, [isOpen, eventCategory]);

    useEffect(() => {
        if (isPostSuccess || isUpdateSuccess) {
            onSuccess?.();
        }
    }, [isPostSuccess, isUpdateSuccess, onSuccess]);

    const onSubmit = async (values: IPreferencesDefaultValues, isExplicitSubmit = false) => {
        if (!userProfile) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return;
        }

        if (isExplicitSubmit) {
            hitGtmSurveyBasicSubmit(values, flowState.gtmSourceSection || eventCategory);
        }

        if (!isExplicitSubmit) {
            hitGtmSurveyClose(flowState.gtmSourceSection || eventCategory);
        }

        const regions = values.regionSelector.map((region) => region.value);

        const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : 1;
        const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : 5;
        const rooms = {lower, upper};

        const price = values.budget && values.budget !== maxBudget ? {lower: 0, upper: values.budget} : null;
        const systemComments = isExplicitSubmit ? "Ankieta świadomie zapisana przez użytkownika" : "Ankieta wysłana poprzez zamknięcie modala";

        const postData: IClientPayload = {
            email: userProfile.email,
            name: userProfile.name,
            phone: userProfile.phone || "",
            marketing_consent: userProfile.email_marketing_consent,
            financing_services: values.creditPlan,
            regions: regions.length ? regions : [],
            rooms: rooms,
            types: [OfferType.FLAT, OfferType.HOUSE],
            price: price || {lower: 0, upper: PREFERENCES_FORM_MAX_DEFAULT},
            area: {lower: 0, upper: 0},
            loan_installment: {lower: 0, upper: 0},
            distance: 0,
            system_comments: systemComments,
            explicit_submit: isExplicitSubmit
        };

        try {
            if (!!clientInfo) {
                await updateClient(postData).unwrap();
            } else {
                // Disable HomeMatch for clients created via this flow
                postData.no_hm_enable = true;

                await postClient(postData).unwrap();
            }

            dispatch(
                savePreferencesVariantB({
                    regions: regions.length ? regions : [],
                    price: !values.budget || values.budget >= maxBudget ? undefined : values.budget,
                    rooms: rooms
                })
            );
        } catch (err) {
            notifyBugsnag(err, "RegistrationPreferencesModal save failed");

            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick(flowState.gtmSourceSection || eventCategory, value, section);
    };

    return (
        <Modal isOpen={isOpen} className={css({sm: {borderRadius: 2}})}>
            <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.WRAPPER}>
                <Modal.Layout.EmptyResponsive closeButtonVariant="none" className={modalStyle}>
                    <div className={contentStyle}>
                        {/* We need to wait until `defaultValues` is built, so that the form gets the correct values */}
                        {isLoading || isPostLoading || isUpdateLoading ? (
                            <div className={loaderWrapperStyle}>
                                <Loader size="lg" />
                            </div>
                        ) : (
                            <UserPreferencesFormProvider defaultValues={defaultValues}>
                                {() => {
                                    return (
                                        <PreferencesFormVariantB
                                            onSubmit={(values, isExplicitSubmit) => onSubmit(values, isExplicitSubmit)}
                                            header={
                                                flowState.titleVariant === AuthModalVariant.CONSTRUCTION_PHOTOS ? null : (
                                                    <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.HEADER_BOX}>
                                                        <PreferencesFormWithPriceHeader
                                                            isPropertyApplication={isPropertyApplication}
                                                            offerId={offerId}
                                                            price={displayedPrice}
                                                        />
                                                    </div>
                                                )
                                            }
                                            topFormContent={
                                                <div className={headerStyle}>
                                                    <div className={headerTextStyle}>
                                                        <Text variant="headline_6" as="p">
                                                            Potwierdź swoje preferencje
                                                        </Text>
                                                        <Text variant="info_txt_1">
                                                            Potwierdź swój budżet lokalizacji i liczbę pokoi, aby otrzymać lepiej spersonalizowane oferty.
                                                        </Text>
                                                    </div>
                                                </div>
                                            }
                                            currency={currency}
                                            maxBudget={maxBudget}
                                            formWrapperClassName={formWrapperClassName}
                                            onFieldInteraction={handleFieldInteraction}
                                            showMarketingConsents
                                            locationFieldTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.LOCATION_FIELD}
                                            budgetInputTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.BUDGET_INPUT}
                                            creditPlanSwitchTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CREDIT_PLAN_SWITCH}
                                            savePreferencesButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.SAVE_PREFERENCES_BUTTON}
                                            closeButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CLOSE_BUTTON}
                                        />
                                    );
                                }}
                            </UserPreferencesFormProvider>
                        )}
                    </div>
                </Modal.Layout.EmptyResponsive>
            </div>
        </Modal>
    );
};

const modalStyle = css({
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    p: 0,

    sm: {
        width: "544px"
    }
});

const contentStyle = css({
    width: "100%",
    p: 0,
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    gap: 4,

    sm: {
        width: "544px",
        p: 3
    }
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "100vh",

    sm: {
        minHeight: "300px"
    }
});

const formWrapperClassName = css({
    p: 2,

    md: {
        p: 0
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
